import clsx from 'clsx';
import React, { MouseEventHandler, MutableRefObject, UIEventHandler, useEffect, useRef, useState } from 'react';

import DeleteIcon from '@/icons/DeleteIcon';
import PopupNg from '@/ui/PopupNg';
import useBodyScrollLock from './useBodyScrollLock';
import useDialogBodyBlockScroll from './useDialogBodyBlockScroll';
import { NuiDialogProps } from 'nike-ui/src/components/dialogs/dialogs';
import { AnimatePresence, motion } from 'framer-motion';
import { motionModalSlideVariant, motionOpacityVariant } from 'nike-ui/src/common/util';

function Dialog({
    children,
    className,
    modal,
    onClose
}: {
    children: React.ReactNode;
    className?: string;
    modal?: boolean;
    onClose?: () => void;
}) {
    useDialogBodyBlockScroll();

    function close() {
        setOverlay(false);
        setTimeout(() => {
            PopupNg.dispatcher.close();
        }, 200);
        if (onClose) {
            onClose();
        }
    }

    const [overlay, setOverlay] = useState(true);

    return (
        <div className={clsx('dialog', className)}>
            <AnimatePresence>
                {overlay && (
                    <motion.div
                        key="backdrop"
                        variants={motionOpacityVariant}
                        initial="initial"
                        animate="enter"
                        exit="exit"
                        className="backdrop"
                        onClick={modal ? null : close}
                    />
                )}
                <Container>{children}</Container>
            </AnimatePresence>
        </div>
    );
}

export function Docked({
    className,
    hideCloseButton,
    modal,
    children,
    onClose,
    ...other
}: {
    children: React.ReactNode;
    className?: string;
    hideCloseButton?: boolean;
    modal?: boolean;
    onClose?: () => void;
}) {
    useDialogBodyBlockScroll();

    function close() {
        setOverlay(false);
        setTimeout(() => {
            PopupNg.dispatcher.close();
        }, 200);
        if (onClose) {
            onClose();
        }
    }

    const [overlay, setOverlay] = useState(true);

    return (
        <AnimatePresence>
            {overlay && (
                <div className={clsx('dialog docked', className)} {...other}>
                    <motion.div
                        key="backdrop"
                        variants={motionOpacityVariant}
                        initial="initial"
                        animate="enter"
                        exit="exit"
                        className="backdrop"
                        onClick={modal ? null : close}
                    />
                    <DockedContainer>
                        {hideCloseButton ? null : <DialogCloseButton onClose={close} />}
                        {children}
                    </DockedContainer>
                </div>
            )}
        </AnimatePresence>
    );
}

export function Fullscreen({
    children,
    onClose,
    className,
    withTransition = true
}: {
    children: React.ReactNode;
    onClose?: () => void;
    className?: string;
    withTransition?: boolean;
}) {
    useDialogBodyBlockScroll();
    const ref = useRef();

    function click(e) {
        if (e.target === ref.current) {
            onClose();
        }
    }

    useEffect(() => {
        const esc = e => {
            if (e.keyCode === 27) {
                onClose();
            }
        };
        document.addEventListener('keyup', esc);
        return () => document.removeEventListener('keyup', esc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={clsx('dialog fullscreen', className)} onClick={click}>
            {withTransition ? (
                <AnimatePresence>
                    <motion.div
                        key="fullscreen"
                        variants={motionOpacityVariant}
                        initial="initial"
                        animate="enter"
                        exit="exit"
                    >
                        <Container ref={ref}>{children}</Container>
                    </motion.div>
                </AnimatePresence>
            ) : (
                <Container ref={ref}>{children}</Container>
            )}
        </div>
    );
}

export const Container = React.memo(
    React.forwardRef(function Container(
        { children }: { children: React.ReactNode },
        ref: MutableRefObject<HTMLDivElement>
    ) {
        return (
            <div className="container" ref={ref}>
                {children}
            </div>
        );
    })
);

export const DockedContainer = React.memo(
    React.forwardRef(function Container(
        { children }: { children: React.ReactNode },
        ref: MutableRefObject<HTMLDivElement>
    ) {
        return (
            <motion.div
                className="container"
                ref={ref}
                variants={motionModalSlideVariant}
                initial="initial"
                animate="enter"
                exit="exit"
            >
                {children}
            </motion.div>
        );
    })
);

export const DialogTitle = React.memo(
    ({
        children,
        className,
        innerRef,
        ...rest
    }: {
        children: React.ReactNode;
        className?: string;
        innerRef?: MutableRefObject<HTMLDivElement>;
    }) => (
        <div className={clsx('dialog-title', className)} ref={innerRef} {...rest}>
            {children}
        </div>
    )
);

export function DialogBody({
    children,
    className,
    noPad,
    ...rest
}: {
    children: React.ReactNode;
    className?: string;
    id?: string;
    onScroll?: UIEventHandler<HTMLDivElement>;
    noPad?: boolean;
}) {
    const ref = useBodyScrollLock();
    return (
        <div className={clsx('dialog-body', className, { 'p-0': noPad })} ref={ref} {...rest}>
            {children}
        </div>
    );
}

export function DialogActions({ children }) {
    return <div className="dialog-actions">{children}</div>;
}

export function DialogButton({
    onClick,
    disabled,
    children,
    className
}: {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
}) {
    return (
        <button className={clsx('dialog-button', className)} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );
}

export function DialogCloseButton({ onClose }: { onClose?: MouseEventHandler<HTMLDivElement> }) {
    return (
        <div className="dialog-close-button" onClick={onClose} data-atid="mobile-dialog-close">
            <DeleteIcon fill="#000" opacity={0.5} height={14} width={14} />
        </div>
    );
}

export const NuiDialog = ({
    title,
    children,
    mobileMode = 'DOCKED',
    buttons,
    className,
    hideCloseButton,
    noPad,
    onClose
}: NuiDialogProps) => {
    const content = (
        <>
            {title && <DialogTitle>{title}</DialogTitle>}
            <DialogBody noPad={noPad}>{children}</DialogBody>
            {buttons && <DialogActions>{buttons /*TODO .map(b => <DialogButton)*/}</DialogActions>}
        </>
    );
    if (mobileMode === 'CENTER') {
        return (
            <Dialog modal={hideCloseButton} className={className}>
                {content}
            </Dialog>
        );
    } else if (mobileMode === 'DOCKED') {
        return (
            <Docked modal={hideCloseButton} hideCloseButton={hideCloseButton} className={className} onClose={onClose}>
                {content}
            </Docked>
        );
    } else {
        return <Fullscreen className={className}>{content}</Fullscreen>;
    }
};

export default Dialog;
